/**
 * Recommendations Component
 * Shows prioritized recommendations for improving accessibility
 */

import { useMemo } from 'react';
import { AlertCircle, Zap, Target, CheckCircle2, ArrowRight } from 'lucide-react';
import { PageResult, AccessibilityIssue, EaaIssue, CoreWebVitals } from '../../types';

interface Recommendation {
  title: string;
  description: string;
  category: 'immediate' | 'quick-win' | 'strategic';
  priority: number;
}

interface IssueCounts {
  critical: number;
  serious: number;
  moderate: number;
  minor: number;
}

interface CategoryScores {
  accessibility?: number;
  eaa?: number;
  seo?: number;
  performance?: number;
}

interface RecommendationsProps {
  pageResults: PageResult[];
  totalIssues: IssueCounts;
  categoryScores?: CategoryScores;
}

export function Recommendations({ pageResults, totalIssues, categoryScores }: RecommendationsProps) {
  // Generate recommendations based on scan results
  const recommendations = useMemo(() => {
    const allAccessibilityIssues = pageResults.flatMap((p) => p.accessibilityIssues || []);
    const allEaaIssues = pageResults.flatMap((p) => p.eaaIssues || []);
    const coreWebVitals = pageResults.find((p) => p.coreWebVitals)?.coreWebVitals;

    return generateRecommendations(
      totalIssues,
      allAccessibilityIssues,
      allEaaIssues,
      coreWebVitals,
      categoryScores
    );
  }, [pageResults, totalIssues, categoryScores]);

  const immediateActions = recommendations.filter((r) => r.category === 'immediate');
  const quickWins = recommendations.filter((r) => r.category === 'quick-win');
  const strategic = recommendations.filter((r) => r.category === 'strategic');

  if (recommendations.length === 0) {
    return (
      <div className="card bg-green-50 border border-green-200">
        <div className="flex items-center gap-3">
          <CheckCircle2 className="w-8 h-8 text-green-600" />
          <div>
            <h3 className="font-semibold text-green-800">Excellent Work!</h3>
            <p className="text-body-sm text-green-700">
              Your website has no critical accessibility issues. Continue monitoring and maintain
              regular testing to ensure ongoing compliance.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div>
        <h2 className="text-heading-2 font-semibold text-dark-navy mb-2">Recommendations</h2>
        <p className="text-body text-gray-medium">
          Prioritized recommendations to help you systematically improve your website's
          accessibility compliance.
        </p>
      </div>

      {/* Immediate Actions */}
      {immediateActions.length > 0 && (
        <RecommendationSection
          title="Immediate Actions"
          icon={<AlertCircle className="w-5 h-5" />}
          recommendations={immediateActions}
          accentColor="red"
          description="Critical issues that completely block users with disabilities. Fix these first."
        />
      )}

      {/* Quick Wins */}
      {quickWins.length > 0 && (
        <RecommendationSection
          title="Quick Wins"
          icon={<Zap className="w-5 h-5" />}
          recommendations={quickWins}
          accentColor="green"
          description="Easy fixes with high impact that can be implemented quickly."
        />
      )}

      {/* Strategic / Next Steps */}
      {strategic.length > 0 && (
        <RecommendationSection
          title="Next Steps"
          icon={<Target className="w-5 h-5" />}
          recommendations={strategic}
          accentColor="coral"
          description="Long-term improvements for ongoing accessibility excellence."
        />
      )}
    </div>
  );
}

interface RecommendationSectionProps {
  title: string;
  icon: React.ReactNode;
  recommendations: Recommendation[];
  accentColor: 'red' | 'green' | 'coral';
  description: string;
}

function RecommendationSection({
  title,
  icon,
  recommendations,
  accentColor,
  description,
}: RecommendationSectionProps) {
  const getColorClasses = () => {
    switch (accentColor) {
      case 'red':
        return {
          bg: 'bg-red-50',
          border: 'border-l-red-500',
          iconBg: 'bg-red-100',
          iconText: 'text-red-600',
          badge: 'bg-red-600',
        };
      case 'green':
        return {
          bg: 'bg-green-50',
          border: 'border-l-green-500',
          iconBg: 'bg-green-100',
          iconText: 'text-green-600',
          badge: 'bg-green-600',
        };
      case 'coral':
        return {
          bg: 'bg-orange-50',
          border: 'border-l-coral',
          iconBg: 'bg-coral/10',
          iconText: 'text-coral',
          badge: 'bg-coral',
        };
    }
  };

  const colors = getColorClasses();

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className={`p-1.5 rounded-lg ${colors.iconBg} ${colors.iconText}`}>{icon}</div>
        <div>
          <h3 className="font-semibold text-dark-navy">{title}</h3>
          <p className="text-body-sm text-gray-medium">{description}</p>
        </div>
      </div>

      <div className="space-y-2">
        {recommendations.map((rec, idx) => (
          <div
            key={`${rec.title}-${idx}`}
            className={`${colors.bg} border-l-4 ${colors.border} rounded-r-lg p-4`}
          >
            <div className="flex items-start gap-3">
              <div
                className={`flex-shrink-0 w-6 h-6 rounded-full ${colors.badge} text-white flex items-center justify-center text-xs font-semibold`}
              >
                {idx + 1}
              </div>
              <div className="flex-grow">
                <h4 className="font-semibold text-dark-navy text-body mb-1">{rec.title}</h4>
                <p className="text-body-sm text-gray-dark">{rec.description}</p>
              </div>
              <ArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0 mt-1" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Generate recommendations based on scan results
 */
function generateRecommendations(
  totalIssues: IssueCounts,
  accessibilityIssues: AccessibilityIssue[],
  eaaIssues: EaaIssue[],
  coreWebVitals?: CoreWebVitals,
  categoryScores?: CategoryScores
): Recommendation[] {
  const recommendations: Recommendation[] = [];

  // IMMEDIATE ACTIONS (Critical issues)
  if (totalIssues.critical > 0) {
    recommendations.push({
      title: `Fix ${totalIssues.critical} Critical Accessibility Issues`,
      description:
        'Critical issues completely block users with disabilities from accessing content. These should be fixed immediately.',
      category: 'immediate',
      priority: 1,
    });
  }

  // Check for specific critical issue types
  const hasImageAltCritical = accessibilityIssues.some(
    (i) => i.impact === 'critical' && (i.ruleId.includes('alt') || i.ruleId.includes('image'))
  );
  const hasFormLabelCritical = accessibilityIssues.some(
    (i) => i.impact === 'critical' && (i.ruleId.includes('label') || i.ruleId.includes('form'))
  );

  if (hasImageAltCritical) {
    recommendations.push({
      title: 'Add Alternative Text to Images',
      description:
        'Images without alt text are invisible to screen reader users. Add descriptive alt text to all meaningful images.',
      category: 'immediate',
      priority: 2,
    });
  }

  if (hasFormLabelCritical) {
    recommendations.push({
      title: 'Label All Form Controls',
      description:
        'Form inputs without labels cannot be identified by screen reader users. Associate labels with all form fields.',
      category: 'immediate',
      priority: 3,
    });
  }

  // EAA Compliance
  if (eaaIssues.length > 5 || (categoryScores?.eaa !== undefined && categoryScores.eaa < 70)) {
    recommendations.push({
      title: 'Address EAA Compliance Gaps',
      description: `${eaaIssues.length} EN 301 549 issues need remediation before the June 2025 EAA deadline.`,
      category: 'immediate',
      priority: 4,
    });
  }

  // QUICK WINS (Easy fixes with high impact)
  const hasContrastIssues = accessibilityIssues.some((i) => i.ruleId.includes('contrast'));
  const hasHeadingIssues = accessibilityIssues.some((i) => i.ruleId.includes('heading'));
  const hasLangIssues = accessibilityIssues.some((i) => i.ruleId.includes('lang'));
  const hasTitleIssues = accessibilityIssues.some((i) => i.ruleId.includes('document-title'));
  const hasLinkNameIssues = accessibilityIssues.some((i) => i.ruleId.includes('link-name'));

  if (hasContrastIssues) {
    recommendations.push({
      title: 'Improve Color Contrast',
      description:
        'Increase contrast ratios to at least 4.5:1 for normal text. This helps users with low vision and improves readability for everyone.',
      category: 'quick-win',
      priority: 5,
    });
  }

  if (hasLangIssues) {
    recommendations.push({
      title: 'Add Language Attribute',
      description:
        'Add lang="en" (or appropriate language code) to the HTML element. This ensures correct pronunciation by screen readers.',
      category: 'quick-win',
      priority: 6,
    });
  }

  if (hasTitleIssues) {
    recommendations.push({
      title: 'Add Descriptive Page Titles',
      description:
        'Ensure every page has a unique, descriptive title element. Titles help users identify pages and navigate between tabs.',
      category: 'quick-win',
      priority: 7,
    });
  }

  if (hasHeadingIssues) {
    recommendations.push({
      title: 'Fix Heading Structure',
      description:
        'Use headings in sequential order (h1, h2, h3) to create a logical document outline. This helps screen reader users navigate.',
      category: 'quick-win',
      priority: 8,
    });
  }

  if (hasLinkNameIssues) {
    recommendations.push({
      title: 'Improve Link Text',
      description:
        'Replace generic "click here" and "read more" links with descriptive text that explains where the link goes.',
      category: 'quick-win',
      priority: 9,
    });
  }

  // STRATEGIC / NEXT STEPS
  if (coreWebVitals?.lcpRating === 'poor') {
    recommendations.push({
      title: 'Optimize Page Load Performance',
      description:
        'Improve Largest Contentful Paint (LCP) by optimizing images, reducing server response times, and implementing lazy loading.',
      category: 'strategic',
      priority: 10,
    });
  }

  if (coreWebVitals?.clsRating === 'poor') {
    recommendations.push({
      title: 'Reduce Layout Shift',
      description:
        'Set explicit dimensions for images and videos, and avoid inserting content above existing content to reduce CLS.',
      category: 'strategic',
      priority: 11,
    });
  }

  if (totalIssues.serious > 5) {
    recommendations.push({
      title: 'Conduct Manual Accessibility Audit',
      description:
        'With multiple serious issues, a comprehensive manual review by an accessibility expert is recommended.',
      category: 'strategic',
      priority: 12,
    });
  }

  recommendations.push({
    title: 'Implement Accessibility Testing in CI/CD',
    description:
      'Add automated accessibility testing to your development pipeline to catch issues before they reach production.',
    category: 'strategic',
    priority: 13,
  });

  recommendations.push({
    title: 'Schedule Regular Accessibility Scans',
    description:
      'Run accessibility scans monthly or after major releases to ensure ongoing compliance and catch regressions early.',
    category: 'strategic',
    priority: 14,
  });

  // Sort by priority and limit
  return recommendations.sort((a, b) => a.priority - b.priority).slice(0, 12);
}
